import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { BarChart3, BookOpen, Check, Loader2, Pencil, Plus, Sparkles, Table as TableIcon, X } from "lucide-react";
import type { EvidenceDocument } from "@lumanity/ui/evidence";
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@lumanity/ui/primitives";
import { useDocumentViewer } from "@lumanity/ui/viewer";
import { cn } from "@/lib/utils";
import AddSourcesDialog from "./AddSourcesDialog";
import type { OutlineMutations } from "./draftApi";
import { startGeneration, useSectionGeneration } from "./generationStreamStore";
import PromptPicker from "./PromptPicker";
import { SOURCE_KIND_LABELS, isDisplayItem, sourceName } from "./sourceModel";
import type { Section, SectionPrompt, SectionSource, SourceKind } from "./types";
import { documentName } from "./useProjectEvidence";

const SOURCE_ICONS: Record<SourceKind, typeof BookOpen> = { document: BookOpen, figure: BarChart3, table: TableIcon };

interface SourceRowProps {
  source: SectionSource;
  document: EvidenceDocument | undefined;
  canWrite: boolean;
  onOpen: () => void;
  onChange: (patch: Partial<Pick<SectionSource, "kind" | "label" | "version_label" | "context">>) => void;
  onRemove: () => void;
}

const SourceRow = ({ source, document, canWrite, onOpen, onChange, onRemove }: SourceRowProps) => {
  const [context, setContext] = useState(source.context);
  const [editing, setEditing] = useState(false);
  const [kind, setKind] = useState<SourceKind>(source.kind);
  const [label, setLabel] = useState(source.label ?? "");
  const [version, setVersion] = useState(source.version_label ?? "");
  const Icon = SOURCE_ICONS[source.kind];
  const name = sourceName(source, document && documentName(document));

  const saveItem = () => {
    setEditing(false);
    onChange({ kind, label: isDisplayItem(kind) ? label : null, version_label: isDisplayItem(kind) ? version : null });
  };

  return (
    <li className="space-y-1.5 border-b pb-3 last:border-b-0">
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-brand-navy" />
        <button type="button" onClick={onOpen} disabled={!document} className="flex-1 min-w-0 text-left text-sm text-blue-700 hover:underline disabled:text-muted-foreground disabled:no-underline">
          <span className="block truncate" title={name}>
            {name}
          </span>
          {isDisplayItem(source.kind) && document && <span className="block truncate text-xs text-muted-foreground">{documentName(document)}</span>}
        </button>
        {canWrite && (
          <>
            <button type="button" onClick={() => setEditing((e) => !e)} className="rounded p-0.5 text-muted-foreground hover:bg-muted" aria-label={`Change how ${name} is used`}>
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button type="button" onClick={onRemove} className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-destructive" aria-label={`Remove ${name}`}>
              <X className="h-3.5 w-3.5" />
            </button>
          </>
        )}
      </div>
      {editing && (
        <div className="space-y-1.5 rounded bg-muted/50 p-2">
          <Select value={kind} onValueChange={(v) => setKind(v as SourceKind)}>
            <SelectTrigger className="h-8 text-xs" aria-label="Use as">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(SOURCE_KIND_LABELS) as SourceKind[]).map((k) => (
                <SelectItem key={k} value={k}>
                  {SOURCE_KIND_LABELS[k]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isDisplayItem(kind) && (
            <div className="flex gap-1.5">
              <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label" aria-label="Display item label" maxLength={120} className="h-8 text-xs" />
              <Input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="Version" aria-label="Display item version" maxLength={60} className="h-8 w-24 text-xs" />
            </div>
          )}
          <Button type="button" size="sm" variant="outline" className="h-7 gap-1" onClick={saveItem}>
            <Check className="h-3.5 w-3.5" /> Done
          </Button>
        </div>
      )}
      <label className="block text-xs text-muted-foreground" htmlFor={`context-${source.id}`}>
        {isDisplayItem(source.kind) ? "Display item instructions" : "Context"}
      </label>
      <Input
        id={`context-${source.id}`}
        value={context}
        disabled={!canWrite}
        onChange={(e) => setContext(e.target.value)}
        onBlur={() => context !== source.context && onChange({ context })}
        placeholder={isDisplayItem(source.kind) ? "What to use or ignore from this item..." : "Add context for this source..."}
        className="h-8 text-sm"
      />
    </li>
  );
};

interface SectionPanelProps {
  section: Section;
  documents: EvidenceDocument[];
  documentsById: Map<string, EvidenceDocument>;
  canWrite: boolean;
  mutations: OutlineMutations;
  onError: (title: string, error: unknown) => void;
}

/**
 * The right panel of the First draft screen for the selected section: its AI
 * instructions (typed, or from the prompt library), word limit and sources,
 * each with the writer's context (FR-5-01 – FR-5-07).
 */
const SectionPanel = ({ section, documents, documentsById, canWrite, mutations, onError }: SectionPanelProps) => {
  const [instructions, setInstructions] = useState(section.instructions);
  const [limit, setLimit] = useState(section.word_limit?.toString() ?? "");
  const [adding, setAdding] = useState(false);
  const { openDocument } = useDocumentViewer<EvidenceDocument>();
  const queryClient = useQueryClient();
  const generation = useSectionGeneration(section.id);

  const update = (patch: Omit<Parameters<OutlineMutations["update"]["mutate"]>[0]["patch"], "version">) =>
    mutations.update.mutate({ id: section.id, patch: { version: section.version, ...patch } }, { onError: (e) => onError("Could not save the section", e) });

  const saveLimit = () => {
    const value = limit.trim() ? Number.parseInt(limit, 10) : null;
    if (value !== null && (!Number.isFinite(value) || value < 1)) {
      setLimit(section.word_limit?.toString() ?? "");
      return;
    }
    if (value !== section.word_limit) update({ word_limit: value });
  };
  const pickPrompt = (prompt: SectionPrompt) => {
    setInstructions(prompt.text);
    update({ instructions: prompt.text, prompt_library_id: prompt.id });
  };
  const addSources = async (sources: Parameters<OutlineMutations["addSource"]["mutateAsync"]>[0]["source"][]) => {
    try {
      for (const source of sources) await mutations.addSource.mutateAsync({ sectionId: section.id, source });
      setAdding(false);
    } catch (e) {
      onError("Could not add the sources", e);
    }
  };
  const sourceChange = (sourceId: string) => (patch: Parameters<OutlineMutations["updateSource"]["mutate"]>[0]["patch"]) =>
    mutations.updateSource.mutate({ sectionId: section.id, sourceId, patch }, { onError: (e) => onError("Could not save the source", e) });

  return (
    <aside className="rounded-lg border bg-[#f6f7f1] p-4 space-y-4 lg:sticky lg:top-6" aria-label={`${section.title} setup`}>
      <h2 className="text-lg font-semibold text-brand-navy">{section.title}</h2>

      <div className="space-y-1.5">
        <label htmlFor="section-instructions" className="text-xs font-semibold text-brand-navy">
          AI Instructions
        </label>
        <div className="flex gap-2">
          <Textarea
            id="section-instructions"
            value={instructions}
            disabled={!canWrite}
            onChange={(e) => setInstructions(e.target.value)}
            // Typed changes are no longer the library prompt's text.
            onBlur={() => instructions !== section.instructions && update({ instructions, prompt_library_id: null })}
            placeholder="Write AI instructions for this section, or choose from the library..."
            className="min-h-[7rem] bg-background text-sm"
          />
          <PromptPicker disabled={!canWrite} onPick={pickPrompt} />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <label htmlFor="section-word-limit" className="text-xs font-semibold text-brand-navy">
          Word limit
        </label>
        <Input
          id="section-word-limit"
          type="number"
          min={1}
          value={limit}
          disabled={!canWrite}
          onChange={(e) => setLimit(e.target.value)}
          onBlur={saveLimit}
          placeholder="None"
          className="h-8 w-24 bg-background text-sm"
        />
        <span className="text-xs text-muted-foreground">{section.word_count} written</span>
      </div>

      <div className="rounded-md border bg-background p-3 space-y-3">
        <h3 className="text-sm font-semibold text-brand-navy">Sources</h3>
        {section.sources.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {section.parent_id ? "None yet: this subsection uses its parent section's sources." : "None yet. Add the documents this section should be grounded in."}
          </p>
        ) : (
          <ul className="space-y-3">
            {section.sources.map((source) => {
              const doc = documentsById.get(source.document_id);
              return (
                <SourceRow
                  key={`${source.id}-${source.kind}-${source.label}-${source.version_label}`}
                  source={source}
                  document={doc}
                  canWrite={canWrite}
                  onOpen={() => doc && openDocument({ doc })}
                  onChange={sourceChange(source.id)}
                  onRemove={() =>
                    mutations.removeSource.mutate({ sectionId: section.id, sourceId: source.id }, { onError: (e) => onError("Could not remove the source", e) })
                  }
                />
              );
            })}
          </ul>
        )}
        {canWrite && (
          <Button type="button" variant="outline" className="w-full gap-1 border-brand-teal text-brand-teal" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" /> Add Sources
          </Button>
        )}
      </div>

      {canWrite && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  type="button"
                  variant="brand"
                  className={cn("w-full gap-2")}
                  disabled={section.sources.length === 0 || generation.pending}
                  onClick={() => startGeneration({ sectionId: section.id, outputId: section.output_id, queryClient })}
                >
                  {generation.pending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Generating…
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" /> Generate
                    </>
                  )}
                </Button>
              </span>
            </TooltipTrigger>
            {section.sources.length === 0 && <TooltipContent>Add at least one source to enable generation.</TooltipContent>}
          </Tooltip>
        </TooltipProvider>
      )}
      {generation.error && <p className="text-sm text-destructive">{generation.error}</p>}
      {generation.statusMessage && <p className="text-sm text-muted-foreground">{generation.statusMessage}</p>}

      <AddSourcesDialog
        open={adding}
        onOpenChange={setAdding}
        sectionTitle={section.title}
        documents={documents}
        usedIds={new Set(section.sources.map((s) => s.document_id))}
        saving={mutations.addSource.isPending}
        onAdd={(sources) => void addSources(sources)}
      />
    </aside>
  );
};

export default SectionPanel;
